import { DevTool } from "@hookform/devtools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "antd";
import React, { PropsWithChildren } from "react";
import { DefaultValues, FieldValues, FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Nullable } from "types/models";
import { ZodType } from "zod";

export interface GeneralFormProviderProps<T extends FieldValues> extends PropsWithChildren {
    schema: ZodType<T>;
    onSubmit?: SubmitHandler<T>;
    defaultValues?: DefaultValues<Nullable<T>>;
    id?: string;
}

const customizeRequiredMark = (label: React.ReactNode, { required }: { required: boolean }) => (
    <>
        {label} {required ? <span className={"ml-1 text-[#b91c1c]"}>*</span> : null}
    </>
);

export function NestedGeneralFormProvider<T extends FieldValues>({
    schema,
    onSubmit = () => null,
    defaultValues,
    children,
    id,
}: GeneralFormProviderProps<T>) {
    const [form] = Form.useForm();

    const methods = useForm<T>({
        defaultValues: defaultValues as DefaultValues<T> | undefined,
        resolver: zodResolver(schema),
        mode: "onChange",
    });

    const { control, getValues, reset } = methods;

    const handleFinish = async () => {
        const structuredData = getValues();
        const res = await onSubmit(structuredData);

        if (res) {
            reset(res as DefaultValues<T>);
        }
    };

    return (
        <FormProvider {...methods}>
            <Form
                id={id}
                onFinish={handleFinish}
                form={form}
                layout="vertical"
                variant="outlined"
                requiredMark={customizeRequiredMark}
                size={"small"}
            >
                {children}
            </Form>
            {import.meta.env.DEV && <DevTool control={control} />}
        </FormProvider>
    );
}
